
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LeadFormFields } from "./form-fields/LeadFormFields";
import { LeadFormActions } from "./form-actions/LeadFormActions";
import { LeadPriority } from "./types";
import { useCompanyEnrichment, TriggerEnrichmentPayload } from "@/hooks/useCompanyEnrichment";

interface LeadFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: any; 
}

interface LeadFormData {
  name: string;
  website: string;
  industry: string;
  size_range: string;
  job_title: string;
  priority: LeadPriority; 
  notes: string;
  source: string;
  geographic_area: string;
  news_monitoring_enabled: boolean;
  corporate_phone: string;
}

export const LeadForm = ({ onClose, onSuccess, initialData }: LeadFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    name: initialData?.name || "",
    website: initialData?.website || "",
    industry: initialData?.industry || "",
    size_range: initialData?.size_range || "not-specified",
    job_title: initialData?.job_title || "",
    priority: initialData?.priority || "medium", 
    notes: initialData?.notes || "",
    source: initialData?.source || "",
    geographic_area: initialData?.geographic_area || "",
    news_monitoring_enabled: initialData?.news_monitoring_enabled ?? true,
    corporate_phone: initialData?.corporate_phone || "",
  });

  const { triggerEnrichmentMutation: triggerEnrichment } = useCompanyEnrichment();

  const handleFormDataChange = (fieldName: keyof LeadFormData, value: string | LeadPriority | boolean) => {
    setFormData(prev => ({ ...prev, [fieldName]: value as any }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!formData.name.trim()) {
        throw new Error("Lead name is required");
      }
      
      if (formData.website && !formData.website.includes(".")) {
        throw new Error("Website URL format is invalid");
      }
      
      const leadPayload = {
        name: formData.name,
        website: formData.website || null,
        industry: formData.industry || null,
        size_range: formData.size_range === "not-specified" ? null : formData.size_range,
        job_title: formData.job_title || null,
        priority: formData.priority,
        notes: formData.notes || null,
        source: formData.source || null,
        geographic_area: formData.geographic_area || null,
        news_monitoring_enabled: formData.news_monitoring_enabled,
        corporate_phone: formData.corporate_phone || null,
      };

      let errorOccurred;
      let newLeadId: string | null = null;

      if (initialData?.id) {
        const { error } = await supabase
            .from("lead_master")
            .update(leadPayload)
            .eq("id", initialData.id);
        errorOccurred = error;
      } else {
        const user = (await supabase.auth.getUser()).data.user;
        if (!user) {
          throw new Error("User not authenticated. Cannot create lead.");
        }
        const { data: newLeadData, error } = await supabase
            .from("lead_master")
            .insert({
              ...leadPayload,
              user_id: user.id,
              status: 'new',
            })
            .select('id')
            .single();
        errorOccurred = error;
        if (newLeadData) {
          newLeadId = newLeadData.id;
        }
      }
      
      if (errorOccurred) throw errorOccurred;
      
      toast.success(initialData?.id ? "Lead updated successfully" : "Lead created successfully");
      
      if (newLeadId && !initialData?.id && formData.name) { // Ensure formData.name exists for company_name
        console.log(`New lead created with ID: ${newLeadId}. Triggering enrichment for ${formData.name}.`);
        const enrichmentPayload: TriggerEnrichmentPayload = {
          lead_id: newLeadId,
          company_name: formData.name, // Use lead's name as company name
          company_website: formData.website || undefined,
        };
        triggerEnrichment(
          enrichmentPayload,
          { 
            onSuccess: () => {
              toast.info("Company enrichment process initiated for the new lead.");
            },
            onError: (enrichError) => {
              console.error("Failed to initiate company enrichment:", enrichError);
              toast.error(`Enrichment initiation failed: ${enrichError.message}`);
            }
          }
        );
      }
      
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save lead");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{initialData?.id ? "Edit Lead" : "Add New Lead"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <LeadFormFields 
            formData={formData}
            onFormDataChange={handleFormDataChange}
          />
          <LeadFormActions 
            onClose={onClose}
            isSubmitting={isSubmitting}
            isEditing={!!initialData?.id}
          />
        </form>
      </CardContent>
    </Card>
  );
};
